import { toRaw } from 'vue';
import { canonicalizeGpioBindings } from '../gpio/GpioBindingModel.js';
import { isEmptyLogicGraph } from './logicGraph.js';
import { compileLogicGraph, mergeObjectActionHandlers } from './logicCompiler.js';
import { deserializeLogicGraph, serializeLogicGraph } from './logicSerialization.js';

/**
 * Adapter between Designer pages (Konva) and the pure logic model.
 *
 * A page holds its graph in `page.logic` (in-memory form, uuid references).
 * A loaded design keeps the file form in `page._pendingLogic` until all of the
 * page's objects exist, then restoreLayerLogic() resolves it.
 *
 * Pages often arrive through Vue-reactive state (the app's layer); every entry
 * point unwraps them, because the graph is plain data that gets structuredClone'd.
 */
const isPageObject = (page, node) => node !== page && typeof node?.obj === 'string' &&
    node.haspid !== undefined && node.haspid !== null && typeof node.id === 'function';

function pageObjects(page) {
    return page?.find?.(node => isPageObject(page, node)) ?? [];
}

// The firmware addresses a page itself as object id 0 (p<n>b0). A regular
// page is an event source too (touch events, swipe gestures); a template page
// is a popup overlay and is not.
export const PAGE_OBJECT_ID = 0;
const pageIsSource = page => page?.obj === 'page' && typeof page.id === 'function';

/** Everything on `page` that can feed an event node: its objects and, for a regular page, the page. */
export function pageLogicSources(page) {
    page = toRaw(page);
    return pageIsSource(page) ? [page, ...pageObjects(page)] : pageObjects(page);
}

/** Lookup context for logicGraph/logicCompiler: resolves only sources on `page`. */
export function createPageLogicContext(page) {
    page = toRaw(page);
    const byUuid = new Map(pageLogicSources(page).map(node => [node.id(), node]));
    return { getObject: uuid => byUuid.get(uuid) ?? null, pageId: Number(page?.haspid) };
}

const haspidOfSource = (page, context) => uuid =>
    (pageIsSource(page) && uuid === page.id() ? PAGE_OBJECT_ID : context.getObject(uuid)?.haspid);

/** Design-save form of a page's logic, or undefined when there is none. */
export function exportPageLogic(page) {
    page = toRaw(page);
    if (page?.logic && !isEmptyLogicGraph(page.logic)) {
        return serializeLogicGraph(page.logic, haspidOfSource(page, createPageLogicContext(page)));
    }
    return page?._pendingLogic ?? undefined;
}

/** Resolve a page's pending file-form logic against its loaded objects. */
export function restorePageLogic(page) {
    page = toRaw(page);
    if (!page?._pendingLogic) return [];
    const uuidByHaspid = new Map(pageObjects(page).map(node => [Number(node.haspid), node.id()]));
    if (pageIsSource(page)) uuidByHaspid.set(PAGE_OBJECT_ID, page.id());
    const { graph, diagnostics } = deserializeLogicGraph(page._pendingLogic, haspid => uuidByHaspid.get(haspid));
    page.logic = graph;
    page._pendingLogic = null;
    return diagnostics.map(message => `Page ${page.haspid}: ${message}`);
}

export function restoreLayerLogic(layer) {
    return (toRaw(layer)?.getChildren?.() ?? [])
        .filter(page => page?._pendingLogic)
        .flatMap(restorePageLogic);
}

// Objects added through the app's reactive layer have a Vue proxy as Konva
// parent, so every step is unwrapped: page.logic must be read as plain data.
function findPage(node) {
    let current = toRaw(node);
    while (current && current.obj !== 'page' && current.obj !== 'template_page') current = toRaw(current.getParent?.());
    return current ?? null;
}

/**
 * The handlers a UI object exports: its stored `node.actions` plus the
 * compiled event/action nodes wired to it. For consumers that must see every
 * action of an object (preview, resource usage, outline indicators).
 */
export function getEffectiveObjectHandlers(node) {
    const raw = toRaw(node);
    const page = pageIsSource(raw) ? raw : findPage(raw?.getParent?.());
    const compiled = page?.logic && !isEmptyLogicGraph(page.logic)
        ? compileLogicGraph(page.logic, createPageLogicContext(page)).handlers.get(raw.id()) ?? []
        : [];
    return mergeObjectActionHandlers(Array.isArray(raw?.actions) ? raw.actions : [], compiled);
}

/** GPIO State Bindings of a UI object that come from GPIO binding nodes of its page. */
export function getLogicGpioStateBindings(node) {
    const raw = toRaw(node);
    const page = findPage(raw?.getParent?.());
    if (!page?.logic || isEmptyLogicGraph(page.logic)) return [];
    return compileLogicGraph(page.logic, createPageLogicContext(page)).stateBindings.get(raw.id()) ?? [];
}

/**
 * Merge the compiled logic of `page` into its runtime export records, as
 * `record.actions` handlers that Exporter._processActions serializes like
 * any other. Only call this on runtime records (never on design-save,
 * clipboard or history records), which are copies of the live model.
 * @returns {string[]} warnings for logic skipped from export
 */
export function attachPageLogicActions(page, records) {
    page = toRaw(page);
    if (!page?.logic || isEmptyLogicGraph(page.logic)) return [];
    const context = createPageLogicContext(page);
    const { handlers, gpioHandlers, stateBindings, diagnostics } = compileLogicGraph(page.logic, context);
    for (const [uuid, list] of handlers) {
        const object = context.getObject(uuid);
        // The page's own handlers go on its header record ({ page } without id).
        const record = object === page
            ? records.find(item => item.id === undefined && Number(item.page) === Number(page.haspid))
            : records.find(item => item.id !== undefined && item.obj === object.obj &&
                Number(item.id) === Number(object.haspid));
        if (record) record.actions = mergeObjectActionHandlers(record.actions, list);
    }
    // GPIO binding nodes extend the object's own GPIO State Bindings.
    for (const [uuid, list] of stateBindings) {
        const object = context.getObject(uuid);
        const record = records.find(item => item.id !== undefined && item.obj === object.obj &&
            Number(item.id) === Number(object.haspid));
        if (!record) continue;
        const current = canonicalizeGpioBindings(record.gpio_bindings) ?? { events: [], state: [] };
        record.gpio_bindings = { ...current, state: [...(current.state ?? []), ...list] };
    }
    const warnings = diagnostics.map(item => `Page ${page.haspid} logic: ${item.message} — skipped from export`);
    // GPIO entity events belong to the page; Exporter serializes them into the
    // header's `gpio_actions`. A template page has no runtime page header.
    if (gpioHandlers.size > 0) {
        const header = pageIsSource(page)
            ? records.find(item => item.id === undefined && Number(item.page) === Number(page.haspid))
            : null;
        if (header) header.gpio_entity_actions = Object.fromEntries(gpioHandlers);
        else warnings.push(`Page ${page.haspid} logic: GPIO events on a template page are not exported`);
    }
    return warnings;
}
